import { AUDIT_ACTIONS, AUDIT_MODULES, type SessionItem, type UserListItem } from "@seagames/contracts";
import { Icons } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { ConfirmWithPasswordModal } from "../../components/common/confirm-with-password-modal";
import { usePageBar } from "../../components/shell/page-bar";
import { ActivityFeed } from "../../components/users/activity-feed";
import { GroupCard } from "../../components/users/group-card";
import { type ActivityFilterDraft, ActivityFiltersBar, toActivityFilters } from "../../components/users/activity-filters";
import { type PasswordReveal, PasswordRevealModal } from "../../components/users/password-reveal-modal";
import { ResetPasswordModal } from "../../components/users/reset-password-modal";
import { SignIns, shortUa } from "../../components/users/sign-ins";
import { Avatar, Kv, KvList, Muted, RvSection, StatusChip, Tag } from "../../components/users/ui";
import { type UserFormTarget, UserFormModal } from "../../components/users/user-form-modal";
import { errorMessage, isTransientError } from "../../lib/api/messages";
import { requireAdminRole } from "../../lib/auth/guards";
import { formatDateTime, formatStamp } from "../../lib/format/datetime";
import { queryClient } from "../../lib/api/query-client";
import { roleLabel, statusLabel } from "../../lib/users/labels";
import {
	invalidateUsers,
	revokeUsersSessions,
	unlockUser,
	userDetailKeys,
	userOptions,
	userSessionsOptions,
} from "../../lib/users/queries";
import { toast } from "../../lib/ui/toast";

const TABS = ["activity", "history", "sign-ins"] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute("/_app/users/$id")({
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	// The open tab AND the activity filters live in the URL, so "what Leon did in Accreditation last week" is a link.
	validateSearch: z.object({
		tab: z.enum(TABS).optional().catch(undefined),
		module: z.enum(Object.keys(AUDIT_MODULES) as [keyof typeof AUDIT_MODULES, ...Array<keyof typeof AUDIT_MODULES>]).optional().catch(undefined),
		action: z.enum(AUDIT_ACTIONS).optional().catch(undefined),
		from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
		to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
		q: z.string().trim().min(1).max(100).optional().catch(undefined),
	}),
	component: UserPage,
});

function UserPage() {
	const { t } = useTranslation(["users", "common"]);
	const { id } = Route.useParams();
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const tab = search.tab ?? "activity";
	const navigate = Route.useNavigate();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const isSelf = session.user.id === id;

	const q = useQuery(userOptions(id));
	const [form, setForm] = useState<UserFormTarget | null>(null);
	const [resetTarget, setResetTarget] = useState<UserListItem | null>(null);
	const [reveal, setReveal] = useState<PasswordReveal | null>(null);

	// Her rule: the name, what it is and the way back live in the shell's top bar — no in-page h1 or back link.
	usePageBar({ title: q.data?.name, subtitle: t("detail.subtitle"), back: { to: "/users", label: t("detail.backToUsers") } });

	// Switching tabs keeps the filters: the same "Accreditation, last week" applies to both activity views.
	// resetScroll: false — on mobile the tabs sit below the fold; jumping to the top on every tab change loses them.
	const setTab = (next: Tab) => void navigate({ search: (prev) => ({ ...prev, tab: next === "activity" ? undefined : next }), replace: true, resetScroll: false });

	const appliedFilters: ActivityFilterDraft = {
		module: search.module ?? "",
		action: search.action ?? "",
		from: search.from ?? "",
		to: search.to ?? "",
		q: search.q ?? "",
	};
	const applyFilters = (d: ActivityFilterDraft) =>
		void navigate({
			search: (prev) => ({
				...prev,
				module: d.module || undefined,
				action: d.action || undefined,
				from: d.from || undefined,
				to: d.to || undefined,
				q: d.q.trim() || undefined,
			}),
			replace: true,
			resetScroll: false,
		});
	const wireFilters = toActivityFilters(appliedFilters);

	const refreshDetail = () => {
		void invalidateUsers();
		void queryClient.invalidateQueries({ queryKey: ["users", "detail", id] });
	};

	const unlock = async () => {
		try {
			await unlockUser(id);
			toast.success(t("detail.unlocked"));
			refreshDetail();
		} catch (err) {
			toast.error(err, t("detail.unlockFailed"));
		}
	};

	if (q.isPending) return <PageSkeleton />;
	if (q.isError) {
		return (
			// Her `.card.empty`: an icon, a 15px title, one line, the way on.
			<div className="grid justify-items-center gap-[0.5rem] rounded-box border border-gms-line bg-base-100 px-[1rem] py-[2.25rem] text-center text-[0.84375rem] text-gms-ink-2">
				<Icons.Alert aria-hidden className="size-[2rem] text-gms-ink-3" />
				<b className="text-[0.9375rem] font-semibold text-gms-navy-ink">{isTransientError(q.error) ? t("detail.errorTransientTitle") : t("detail.errorTitle")}</b>
				<span>{errorMessage(q.error)}</span>
				<div className="mt-[0.25rem] flex flex-wrap justify-center gap-[0.5rem]">
					<button type="button" className="btn btn-sm btn-primary" onClick={() => void q.refetch()}>
						{t("detail.tryAgain")}
					</button>
					<Link to="/users" className="btn btn-sm btn-line">
						{t("detail.backToUsers")}
					</Link>
				</div>
			</div>
		);
	}
	const u = q.data;

	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			{/* ── Hero: the name is in the top bar; here the who-at-a-glance and the actions ── */}
			<header className="flex flex-wrap items-center justify-between gap-[0.75rem]">
				<div className="flex min-w-0 items-center gap-[0.875rem]">
					<Avatar name={u.name} size="lg" />
					<div className="flex min-w-0 flex-col gap-[0.375rem]">
						<span className="truncate text-[0.875rem] text-gms-ink-2">{u.username}</span>
						<div className="flex flex-wrap items-center gap-[0.375rem]">
							<StatusChip status={u.status}>{statusLabel(t, u.status)}</StatusChip>
							{u.mfa_enabled && <Tag tone="ok">{t("detail.badge2faOn")}</Tag>}
							{u.must_change_password && <Tag tone="warn">{t("detail.badgePasswordPending")}</Tag>}
							{isSelf && <Tag tone="muted">{t("detail.badgeYou")}</Tag>}
						</div>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-[0.5rem]">
					<button type="button" className="btn btn-line btn-sm gap-[0.375rem]" onClick={() => void unlock()} title={t("bulk.unlockTitle")}>
						<Icons.Unlock className="size-[1rem]" />
						{t("detail.unlock")}
					</button>
					{isSuper && (
						<>
							{!isSelf && (
								<button
									type="button"
									className="btn btn-line btn-sm btn-square"
									aria-label={t("detail.resetPassword")}
									title={t("detail.resetPassword")}
									onClick={() => setResetTarget(u)}
								>
									<Icons.Key className="size-[1rem]" />
								</button>
							)}
							<button type="button" className="btn btn-sm btn-primary gap-[0.375rem]" onClick={() => setForm({ mode: "edit", user: u })}>
								<Icons.Pencil className="size-[1rem]" />
								{t("detail.edit")}
							</button>
						</>
					)}
				</div>
			</header>

			{/* ── Body ──────────────────────────────────────────────────── */}
			<div className="grid items-start gap-[0.75rem] lg:grid-cols-[21.25rem_minmax(0,1fr)] lg:gap-[1.25rem]">
				<aside className="flex min-w-0 flex-col gap-[0.75rem]">
					<RvSection title={t("detail.profile.title")} flush>
						<KvList>
							<Kv label={t("detail.profile.email")}>{u.email ?? <Muted>—</Muted>}</Kv>
							<Kv label={t("detail.profile.phone")} valueClassName="tabular-nums">
								{u.phone ?? <Muted>—</Muted>}
							</Kv>
							<Kv label={t("detail.profile.roles")}>
								<span className="flex flex-wrap gap-[0.25rem]">
									{u.roles.map((r) => (
										<Tag key={r.code}>{roleLabel(t, r.code)}</Tag>
									))}
								</span>
							</Kv>
							<Kv label={t("detail.profile.lastSignIn")} title={u.last_login_at ? formatDateTime(u.last_login_at) : undefined}>
								{u.last_login_at ? formatStamp(u.last_login_at) : <Muted>{t("never")}</Muted>}
							</Kv>
							<Kv label={t("detail.profile.created")} title={formatDateTime(u.created_at)}>
								{formatStamp(u.created_at)}
							</Kv>
							<Kv label={t("detail.profile.updated")} title={formatDateTime(u.updated_at)}>
								{formatStamp(u.updated_at)}
							</Kv>
							<Kv label={t("detail.profile.id")} valueClassName="font-mono text-[0.6875rem] font-normal break-all text-gms-ink-3">
								{u.id}
							</Kv>
						</KvList>
					</RvSection>

					<GroupCard user={u} canEdit={isSuper} onChanged={refreshDetail} />

					{isSuper && <SessionsCard userId={id} userName={u.name} isSelf={isSelf} />}
				</aside>

				{/* min-w-0: without it the unwrappable tab row widens this grid column past the
				    viewport on mobile instead of scrolling inside its overflow-x-auto strip. */}
				<section className="min-w-0 overflow-hidden rounded-box border border-gms-line bg-base-100">
					{/* Her `.seg`: a soft track, the pressed tab white. Scrolls sideways inside itself on a narrow phone. */}
					<div className="overflow-x-auto border-b border-gms-line px-[1rem] py-[0.75rem] [scrollbar-width:none]">
						<div
							role="tablist"
							aria-label={t("detail.tabs.ariaLabel")}
							className="grid w-full min-w-max auto-cols-[minmax(max-content,1fr)] grid-flow-col gap-[0.25rem] rounded-field bg-gms-soft-2 p-[0.25rem]"
						>
						<TabButton active={tab === "activity"} onClick={() => setTab("activity")}>
							{t("detail.tabs.activity", { name: firstName(u.name) })}
						</TabButton>
						<TabButton active={tab === "history"} onClick={() => setTab("history")}>
							{t("detail.tabs.history")}
						</TabButton>
						<TabButton active={tab === "sign-ins"} onClick={() => setTab("sign-ins")}>
							{t("detail.tabs.signIns")}
						</TabButton>
						</div>
					</div>
					{tab !== "sign-ins" && <ActivityFiltersBar applied={appliedFilters} onApply={applyFilters} />}
					<div className="px-[1rem] py-[1rem]">
						{tab === "activity" && <ActivityFeed key="actor" userId={id} scope="actor" selfName={firstName(u.name)} filters={wireFilters} />}
						{tab === "history" && <ActivityFeed key="subject" userId={id} scope="subject" selfName={firstName(u.name)} filters={wireFilters} />}
						{tab === "sign-ins" && <SignIns userId={id} onUnlock={() => void unlock()} />}
					</div>
				</section>
			</div>

			{isSuper && (
				<>
					<UserFormModal
						target={form}
						onClose={() => {
							setForm(null);
							refreshDetail();
						}}
						selfId={session.user.id}
						onReveal={setReveal}
						onResetPassword={(target) => {
							setForm(null);
							setResetTarget(target);
						}}
					/>
					<ResetPasswordModal
						user={resetTarget}
						onClose={() => {
							setResetTarget(null);
							refreshDetail();
						}}
						onReveal={setReveal}
					/>
					<PasswordRevealModal reveal={reveal} onClose={() => setReveal(null)} />
				</>
			)}
		</div>
	);
}

// ── Sessions (SUPER_ADMIN) ──────────────────────────────────────────────────

const SESSIONS_SHOWN_STEP = 5;

/**
 * SUPER_ADMIN only (the parent renders it for no one else). "Sign out all" is
 * the bulk revoke with this one id, confirmed with the admin's own password —
 * and absent on your own account: the API refuses self (CANNOT_TARGET_SELF),
 * and signing yourself out everywhere belongs to your own profile, not here.
 */
function SessionsCard({ userId, userName, isSelf }: { userId: string; userName: string; isSelf: boolean }) {
	const { t } = useTranslation(["users", "common"]);
	const q = useQuery(userSessionsOptions(userId));
	const [confirming, setConfirming] = useState(false);
	const [shown, setShown] = useState(SESSIONS_SHOWN_STEP);
	const items = q.data?.items ?? [];
	const hidden = items.length - shown;

	const revokeAll = async (password: string) => {
		const r = await revokeUsersSessions([userId], password);
		toast.success(t("sessions.signedOutEverywhere", { name: userName, count: r.sessions_revoked }));
		void queryClient.invalidateQueries({ queryKey: userDetailKeys.sessions(userId) });
	};

	return (
		<>
			<RvSection
				title={t("sessions.title")}
				aside={
					items.length > 0 && !isSelf ? (
						<button
							type="button"
							className="inline-flex h-[2rem] cursor-pointer items-center rounded-[0.5rem] px-[0.625rem] text-[0.8125rem] font-semibold text-gms-bad hover:bg-gms-bad-soft"
							onClick={() => setConfirming(true)}
						>
							{t("sessions.signOutAll")}
						</button>
					) : undefined
				}
			>
				{q.isPending ? (
					<p className="text-[0.8125rem] text-gms-ink-3">{t("common:state.loading")}</p>
				) : q.isError ? (
					<p className="text-[0.8125rem] text-gms-bad">{errorMessage(q.error)}</p>
				) : items.length === 0 ? (
					<p className="text-[0.8125rem] text-gms-ink-3">{t("sessions.empty")}</p>
				) : (
					<>
						<ul className="flex flex-col">
							{items.slice(0, shown).map((s) => (
								<SessionRow key={s.sid} s={s} />
							))}
						</ul>
						{hidden > 0 && (
							<button
								type="button"
								className="btn btn-xs btn-ghost mt-[0.5rem] w-full text-gms-ink-2"
								onClick={() => setShown((n) => n + SESSIONS_SHOWN_STEP)}
							>
								{t("sessions.showMore", { count: hidden })}
							</button>
						)}
					</>
				)}
			</RvSection>
			<ConfirmWithPasswordModal
				open={confirming && !isSelf}
				onClose={() => setConfirming(false)}
				title={t("sessions.confirm.title")}
				description={
					<Trans
						t={t}
						i18nKey="sessions.confirm.body"
						values={{ name: userName }}
						components={{ strong: <strong className="font-medium text-gms-navy-ink" /> }}
					/>
				}
				confirmLabel={t("sessions.confirm.submit")}
				errorPrefix={t("sessions.signOutFailed")}
				onConfirm={revokeAll}
			/>
		</>
	);
}

function SessionRow({ s }: { s: SessionItem }) {
	const { t } = useTranslation(["users", "common"]);
	return (
		<li className="flex items-start gap-[0.625rem] border-t border-gms-line py-[0.5625rem] text-[0.8125rem] leading-snug first:border-t-0 first:pt-0">
			<span aria-hidden className={`mt-[0.375rem] size-[0.5rem] shrink-0 rounded-full ${s.recently_active ? "bg-gms-ok" : "bg-gms-line-2"}`} title={s.recently_active ? t("sessions.activeNow") : t("sessions.idle")} />
			<div className="min-w-0 flex-1">
				<p className="truncate">
					<span className="font-semibold text-gms-navy-ink">{s.user_agent ? shortUa(t, s.user_agent) : t("sessions.unknownDevice")}</span>
					<span className="text-gms-ink-3"> · </span>
					<span className="text-gms-ink-2">{roleLabel(t, s.active_role)}</span>
				</p>
				<p className="text-[0.75rem] text-gms-ink-3">
					{s.ip ?? "—"} · {t("sessions.seen", { stamp: formatStamp(s.last_seen_at) })}
				</p>
			</div>
		</li>
	);
}

// ── Bits ────────────────────────────────────────────────────────────────────

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
	return (
		<button
			type="button"
			role="tab"
			aria-selected={active}
			onClick={onClick}
			className={`flex min-h-[2.5rem] cursor-pointer items-center justify-center rounded-[0.375rem] px-[0.75rem] text-[0.84375rem] whitespace-nowrap outline-none transition-colors focus-visible:ring-2 focus-visible:ring-gms-navy/40 ${
				active
					? "bg-base-100 font-semibold text-gms-navy shadow-[0_1px_2px_rgba(18,20,63,0.12),0_0_0_1px_var(--color-gms-line)]"
					: "font-medium text-gms-ink-2 hover:text-gms-navy-ink"
			}`}
		>
			{children}
		</button>
	);
}

function PageSkeleton() {
	return (
		<div aria-busy className="flex flex-col gap-[1.25rem]">
			<div className="flex items-center gap-[1rem]">
				<span className="skeleton size-[3.5rem] rounded-full" />
				<div className="flex flex-col gap-[0.5rem]">
					<span className="skeleton h-[1.5rem] w-[14rem]" />
					<span className="skeleton h-[0.875rem] w-[10rem]" />
				</div>
			</div>
			<div className="grid gap-[1.25rem] lg:grid-cols-[20rem_minmax(0,1fr)]">
				<span className="skeleton h-[14rem] rounded-box" />
				<span className="skeleton h-[20rem] rounded-box" />
			</div>
		</div>
	);
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;
